import { useAuthStore } from '../stores/auth-store'

/**
 * Composable para verificar permisos del usuario autenticado.
 * Proporciona funciones helpers para verificar permisos granulares
 * basados en el patrón {acción}.{módulo}
 *
 * @example
 * const { canCreate, canView, hasPermission } = usePermissions()
 * if (canCreate('products')) {
 *   // Mostrar botón de crear producto
 * }
 */
export function usePermissions() {
  const authStore = useAuthStore()

  return {
    // Verificadores básicos
    /**
     * Verifica si el usuario tiene un permiso específico
     * @param permission - Nombre del permiso (ej: 'view.products')
     */
    hasPermission: (permission: string) => authStore.hasPermission(permission),

    /**
     * Verifica si el usuario tiene al menos uno de los permisos (OR)
     * @param permissions - Array de permisos
     */
    hasAnyPermission: (permissions: string[]) => authStore.hasAnyPermission(permissions),

    /**
     * Verifica si el usuario tiene todos los permisos (AND)
     * @param permissions - Array de permisos
     */
    hasAllPermissions: (permissions: string[]) => authStore.hasAllPermissions(permissions),

    // Helpers por módulo y acción
    /**
     * Verifica si el usuario puede acceder a un módulo
     * @param module - Nombre del módulo (ej: 'products')
     * @returns true si tiene permiso 'access.{module}'
     */
    canAccess: (module: string) => authStore.canAccess(module),

    /**
     * Verifica si el usuario puede ver/listar registros de un módulo
     * @param module - Nombre del módulo (ej: 'products')
     * @returns true si tiene permiso 'view.{module}'
     */
    canView: (module: string) => authStore.canView(module),

    /**
     * Verifica si el usuario puede crear registros en un módulo
     * @param module - Nombre del módulo (ej: 'products')
     * @returns true si tiene permiso 'create.{module}'
     */
    canCreate: (module: string) => authStore.canCreate(module),

    /**
     * Verifica si el usuario puede actualizar registros de un módulo
     * @param module - Nombre del módulo (ej: 'products')
     * @returns true si tiene permiso 'update.{module}'
     */
    canUpdate: (module: string) => authStore.canUpdate(module),

    /**
     * Verifica si el usuario puede eliminar registros de un módulo
     * @param module - Nombre del módulo (ej: 'products')
     * @returns true si tiene permiso 'delete.{module}'
     */
    canDelete: (module: string) => authStore.canDelete(module),

    /**
     * Verifica si el usuario puede restaurar registros eliminados de un módulo
     * @param module - Nombre del módulo (ej: 'products')
     * @returns true si tiene permiso 'restore.{module}'
     */
    canRestore: (module: string) => authStore.canRestore(module),
  }
}
